import React, {useEffect, useState} from "react";
import { FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import HistoryTable from "./HistoryTable";
import { FilterLineIcon } from "../../../components/common/Icons/Icons";

// URL params the filter panel owns (counted on the Filters button). Dates travel as epoch
// milliseconds, the same shape the Log History filters used.
const filterParams = ['status', 'created_since', 'created_until', 'updated_since', 'updated_until'];

const STATUS_OPTIONS: [string, string][] = [
  ['fulfilled', 'Fulfilled'],
  ['sent', 'Sent'],
  ['pending', 'Pending'],
  ['error', 'Error'],
];

const toDay = (param: string | null) => param ? dayjs(parseInt(param)).format("YYYY-MM-DD") : '';
const toMs = (day: string) => day ? String(dayjs(day).valueOf()) : '';

const UpdateHistoryContainer: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Open by default: every filter in view
  const [showFilters, setShowFilters] = useState(true);

  // Pending filter state — only committed to the URL (and so to the fetch) on Apply
  const [status, setStatus] = useState('');
  const [sentFrom, setSentFrom] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [updatedFrom, setUpdatedFrom] = useState('');
  const [updatedTo, setUpdatedTo] = useState('');

  useEffect(() => {
    setStatus(searchParams.get('status') ?? '');
    setSentFrom(toDay(searchParams.get('created_since')));
    setSentTo(toDay(searchParams.get('created_until')));
    setUpdatedFrom(toDay(searchParams.get('updated_since')));
    setUpdatedTo(toDay(searchParams.get('updated_until')));
  }, [searchParams]);

  const handleFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('page'); //reset page
    const values: Record<string, string> = {
      status,
      created_since: toMs(sentFrom),
      created_until: toMs(sentTo),
      updated_since: toMs(updatedFrom),
      updated_until: toMs(updatedTo),
    };
    Object.entries(values).forEach(([key, value]) => value === '' ? next.delete(key) : next.set(key, value));
    setSearchParams(next);
  };

  const handleClear = () => {
    setStatus(''); setSentFrom(''); setSentTo(''); setUpdatedFrom(''); setUpdatedTo('');
    const next = new URLSearchParams(searchParams);
    ['page', ...filterParams].forEach((key) => next.delete(key));
    setSearchParams(next);
  };

  const activeFilters = filterParams.filter((param) => searchParams.has(param)).length;
  const today = dayjs().format("YYYY-MM-DD");

  const dateField = (id: string, label: string, value: string, setValue: (v: string) => void, min?: string, max?: string) => (
    <TextField
      id={id}
      label={label}
      type="date"
      size="small"
      fullWidth
      value={value}
      onChange={(e) => setValue(e.target.value)}
      InputLabelProps={{ shrink: true }}
      inputProps={{ min: min || undefined, max: max || today }}
    />
  );

  const toolbar = (
    <button
      type="button"
      className={`btn btn-sm dt-tool-btn lp-filter-toggle ${showFilters ? 'active' : ''}`}
      onClick={() => setShowFilters(!showFilters)}
      aria-expanded={showFilters}
      aria-controls="lp-filters"
    >
      <FilterLineIcon/> Filters
      {activeFilters > 0 && <span className="lp-count">{activeFilters}</span>}
    </button>
  );

  return (
    <div className={`lp-layout ${showFilters ? '' : 'filters-hidden'}`}>
      {showFilters && (
        <aside className="lp-filters" id="lp-filters" aria-label="Filters">
          <div className="lp-filters-body">
            <div className="lp-group">
              <FormControl fullWidth size="small">
                <InputLabel id="status-filter-label">Status</InputLabel>
                <Select
                  labelId="status-filter-label"
                  label="Status"
                  value={status}
                  onChange={(e) => setStatus(String(e.target.value))}
                >
                  <MenuItem value="">All</MenuItem>
                  {STATUS_OPTIONS.map(([value, text]) => <MenuItem key={value} value={value}>{text}</MenuItem>)}
                </Select>
              </FormControl>
            </div>

            <div className="lp-group">
              <span className="lp-side-title">Sent</span>
              <div className="lp-range">
                {dateField("sent-from", "From", sentFrom, setSentFrom, undefined, sentTo)}
                {dateField("sent-to", "To", sentTo, setSentTo, sentFrom)}
              </div>
            </div>

            <div className="lp-group">
              <span className="lp-side-title">Updated</span>
              <div className="lp-range">
                {dateField("updated-from", "From", updatedFrom, setUpdatedFrom, undefined, updatedTo)}
                {dateField("updated-to", "To", updatedTo, setUpdatedTo, updatedFrom)}
              </div>
            </div>
          </div>

          <div className="lp-filters-foot">
            <button type="button" className="btn btn-sm lp-clear-btn" onClick={handleClear}>Clear</button>
            <button type="button" className="btn btn-sm btn-primary" onClick={handleFilter}>Apply Filters</button>
          </div>
        </aside>
      )}

      <div className="dt">
        <div className="dt-toolbar">
          <div className="dt-toolbar-start">{toolbar}</div>
        </div>
        <HistoryTable/>
      </div>
    </div>
  );
};

export default UpdateHistoryContainer;
